import { Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

import { lineItemFormText, POPULATION_DENSITY_PACING_SEED_TYPE_LABEL } from '../../constants/lineItems';
import { useConstants } from '../../hooks/useConstants';
import { LineItem } from '../../validationSchemas/LineItemSchema';
import AdGroupSelector from '../forms/AdGroupSelector';
import CheckboxArray from '../forms/CheckboxArray';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import TextField from '../forms/TextField';
import LineItemKPI from './LineItemKPI';

// These are the optimization IDs that must be disabled when population density pacing is enabled
const POPULATION_DENSITY_PACING_DISABLED_OPTIMIZATION_IDS: number[] = [2, 4];

function LineItemObjectivesSection() {
  const { getValues }: UseFormReturn<LineItem> = useFormContext();
  const lineItemId = getValues('lineItemId');
  const kpiObjectives = getValues('kpiObjectives');
  let defaultHasPopulationDensityPacing = false;
  if (kpiObjectives?.find((item) => item.name === POPULATION_DENSITY_PACING_SEED_TYPE_LABEL)) {
    defaultHasPopulationDensityPacing = true;
  }
  const [hasPopulationDensityPacing, setPopulationDensityPacing] = useState<boolean>(defaultHasPopulationDensityPacing);

  const { constants } = useConstants();
  const OPTIMIZATION_TYPES = useMemo(
    () =>
      (constants?.INSERTION_ORDER?.OPTIMIZATION_TYPES || []).map(([value, label]) => ({
        value,
        label,
        disabled: POPULATION_DENSITY_PACING_DISABLED_OPTIMIZATION_IDS.includes(value) && hasPopulationDensityPacing,
      })),
    [hasPopulationDensityPacing, constants?.INSERTION_ORDER?.OPTIMIZATION_TYPES]
  );

  // Use isPopulationDensityPacingAdGroupsFieldReady instead of hasPopulationDensityPacing
  // because the `name` for AdGroupSelector, and if the row hasn't been added to kpiObjectives,
  // kpiObjectives.1.adGroupIds may end up being a kpiObjective[1].adGroups = {}
  // instead of kpiObjective[1] = { adGroupIds: [] }
  const isPopulationDensityPacingAdGroupsFieldReady =
    kpiObjectives && kpiObjectives[1]?.name === POPULATION_DENSITY_PACING_SEED_TYPE_LABEL;

  return (
    <FormSection title={lineItemFormText.sectionTitles.objectives}>
      <FormSubsection>
        <Stack spacing={2}>
          <TextField fullWidth label="Line Item Name" name="lineItemName" />
          <TextField label="IO Number" name="insertionOrderNumber" sx={{ width: '33.33%' }} />
          <LineItemKPI
            hasPopulationDensityPacing={hasPopulationDensityPacing}
            setPopulationDensityPacing={setPopulationDensityPacing}
            populationDensityPacingDisabledOptimizationsIds={POPULATION_DENSITY_PACING_DISABLED_OPTIMIZATION_IDS}
          />
          <Typography>Automatic Optimizations</Typography>
          <CheckboxArray row name="optimizationTypeIds" options={OPTIMIZATION_TYPES} />
          {isPopulationDensityPacingAdGroupsFieldReady && (
            <>
              <Typography>Population Density Seed Ad Groups</Typography>
              <AdGroupSelector
                lineItemId={lineItemId}
                name="kpiObjectives.1.adGroupIds"
                label=""
                placeholder="Add Population Density Seed Ad Groups"
              />
            </>
          )}
        </Stack>
      </FormSubsection>
    </FormSection>
  );
}

export default LineItemObjectivesSection;
